import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { AdminProductApiService } from '../api/services/admin-product-api.service';
import { map, Observable, tap } from 'rxjs';
import {
  executeOrNull,
  Guid,
  isEmptyArray,
  once,
  valueOrDefault,
} from '@web/utils';
import {
  AdminProductAttributes,
  AdminProductCharacteristics,
  AdminProductDetailsForm,
} from '../models/admin-product-details-form';
import { AdminProductCreateRequest } from '../api/requests/admin-product-create.request';
import { AdminProductCharacteristicRequest } from '../api/requests/admin-product-characteristic.request';
import { AdminDictionaryProductAttributeRequest } from '@web/shared/module/dictionary';
import { AdminProductFileRequest } from '../api/requests/admin-product-file.request';
import {
  AdminMultimediaImageUpload,
  multimediaFromResponse,
} from '@web/shared/module/multimedia';
import { AdminProductUpdateRequest } from '../api/requests/admin-product-update.request';
import { AdminProductDetails } from '../models/admin-product-details';
import { AdminProductCharacteristicDetails } from '../models/admin-product-characteristic-details';
import { AdminProductAttributeDetails } from '../models/admin-product-attribute-details';
import { AdminProductCollectionFilter } from '../models/admin-product-collection-filter';
import { AdminProductCollectionItem } from '../models/admin-product-collection-item';

@Injectable({
  providedIn: 'root',
})
export class AdminProductDataSourceService {
  private readonly adminProductApi = inject(AdminProductApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminProductCollectionFilter>,
  ): Observable<TableDataPayload<AdminProductCollectionItem>> {
    /* prettier-ignore */
    return this.adminProductApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) => builder.equalFor('type').build())
              .appendText((builder) => builder.equalFor('availableQuantity').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('slug').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('cosmeticBrandName').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminProductCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    cosmeticBrandId: item.cosmeticBrand?.id,
                    cosmeticBrandName: item.cosmeticBrand?.name,
                    createdAt: item.createdAt,
                    featuredFileAlt: item.featuredFile?.alt(),
                    featuredFileUrl: item.featuredFile?.url,
                    id: item.id,
                    name: item.name,
                    type: item.type,
                    price: item.price,
                    availableQuantity: item.availableQuantity,
                    slug: item.slug,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public removeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany kosmetyk?')
          .accept(() => {
            this.adminProductApi
              .removeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrany kosmetyk został usunięty pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: AdminProductDetailsForm): Observable<void> {
    return this.adminProductApi.create(
      new AdminProductCreateRequest({
        name: data.name,
        password: data.password,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        cosmeticBrandId: data.cosmeticBrandId,
        featuredFileId: data.featuredFile?.id,
        status: data.status,
        virtual: data.virtual,
        maxQuantityPerOrder: data.maxQuantityPerOrder,
        availableQuantity: data.availableQuantity,
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminProductFileRequest({ id })),
          ),
          [],
        ),
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminProductCharacteristicRequest({
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
        warehouseGoodId: data.warehouseProductId,
      }),
    );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(
    id: string,
  ): Observable<AdminProductDetailsForm> {
    return this.adminProductApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminProductDetailsForm({
            canBeWarehouseProductIdEdited: response.contextOperations.canBeWarehouseGoodIdEdited,
            name: response.name,
            password: response.password,
            price: response.price,
            attributes: executeOrNull(
              response.attributes,
              (attributes) => new AdminProductAttributes({
                items: attributes.map(({ value, id, productAttribute }) => ({
                  productAttributeId: productAttribute.id,
                  id,
                  content: value,
                })),
              }),
            ),
            characteristics: executeOrNull(
              response.characteristics,
              (characteristics) => new AdminProductCharacteristics({
                items: characteristics.map(({ label, id, value }) => ({
                  label,
                  id,
                  content: value,
                })),
              }),
            ),
            slug: response.slug,
            status: response.status,
            virtual: response.virtual,
            maxQuantityPerOrder: response.maxQuantityPerOrder,
            availableQuantity: response.availableQuantity,
            cosmeticBrandId: response.cosmeticBrand?.id,
            featuredFile: executeOrNull(response.featuredFile?.id, (id) => new AdminMultimediaImageUpload({ id })),
            warehouseProductId: response.warehouseGood?.id,
            files: executeOrNull(response.files, (files) => {
              if (isEmptyArray(files)) {
                return null;
              }

              return files.map(({ id }) => new AdminMultimediaImageUpload({ id }));
            }),
            id: response.id,
          }),
      ),
    );
  }

  public updateByFormData(
    id: Guid,
    data: AdminProductDetailsForm,
  ): Observable<void> {
    return this.adminProductApi.update(
      id,
      new AdminProductUpdateRequest({
        name: data.name,
        password: data.password,
        cosmeticBrandId: data.cosmeticBrandId,
        featuredFileId: data.featuredFile?.id,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        status: data.status,
        virtual: data.virtual,
        maxQuantityPerOrder: data.maxQuantityPerOrder,
        availableQuantity: data.availableQuantity,
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminProductFileRequest({ id })),
          ),
          [],
        ),
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              id: attribute.id,
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminProductCharacteristicRequest({
              id: characteristic.id,
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
        warehouseGoodId: data.warehouseProductId,
        slug: data.slug,
      }),
    );
  }

  public fetchDetailsById(id: Guid): Observable<AdminProductDetails> {
    return this.adminProductApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminProductDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            cosmeticBrandId: response.cosmeticBrand?.id,
            cosmeticBrandName: response.cosmeticBrand?.name,
            createdAt: response.createdAt,
            characteristics: valueOrDefault(
              executeOrNull(response.characteristics, (characteristics) =>
                characteristics.map(
                  (characteristic) =>
                    new AdminProductCharacteristicDetails({
                      id: characteristic.id,
                      content: characteristic.value,
                      label: characteristic.label,
                    }),
                ),
              ),
              [],
            ),
            attributes: valueOrDefault(
              executeOrNull(response.attributes, (attributes) =>
                attributes.map(
                  (attribute) =>
                    new AdminProductAttributeDetails({
                      id: attribute.id,
                      content: attribute.value,
                      label: attribute.productAttribute.label,
                    }),
                ),
              ),
              [],
            ),
            featuredFile: executeOrNull(response.featuredFile, (file) =>
              multimediaFromResponse(file),
            ),
            id: response.id,
            name: response.name,
            password: response.password,
            price: response.price,
            slug: response.slug,
            status: response.status,
            virtual: response.virtual,
            maxQuantityPerOrder: response.maxQuantityPerOrder,
            availableQuantity: response.availableQuantity,
            files: valueOrDefault(
              executeOrNull(response.files, (files) =>
                files.map((file) => multimediaFromResponse(file)),
              ),
              [],
            ),
            warehouseProductName: response.warehouseGood?.name,
          }),
      ),
    );
  }
}
